import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

interface ExperimentErrorParams {
	errorType: ErrorType;
	errorMessage: string;
	severity: Severity;
	source: string;
	cohort?: string;
	additionalAttributes?: Record<string, any>;
}

interface UseExperimentErrorAnalyticsOptions {
	experimentKey?: string;
	defaultAdditionalAttributes?: Record<string, any>;
}

export const useExperimentErrorAnalytics = (options: UseExperimentErrorAnalyticsOptions = {}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		experimentKey = 'upgrade-free-pool-target-experiment',
		defaultAdditionalAttributes = {},
	} = options;

	const trackError = useCallback(
		(params: ExperimentErrorParams) => {
			const {
				errorType,
				errorMessage,
				severity,
				source,
				cohort,
				additionalAttributes = {},
			} = params;

			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey,
					cohort,
				},
				errorType,
				errorMessage,
				severity,
				source,
				additionalAttributes: {
					...defaultAdditionalAttributes,
					...additionalAttributes,
				},
			});

			createAnalyticsEvent(eventPayload).fire();
		},
		[createAnalyticsEvent, experimentKey, defaultAdditionalAttributes],
	);

	const fireExperimentExposureEvent = useCallback(
		(config: { cohort: string; experimentId: string; actionSubjectId: string; source: string }) => {
			try {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'exposed',
						actionSubject: 'experiment',
						actionSubjectId: config.actionSubjectId,
						attributes: {
							cohort: config.cohort,
							...defaultAdditionalAttributes,
						},
						source: config.source,
					},
				}).fire();
				return true; // success
			} catch (error) {
				trackError({
					errorType: ErrorType.GENERIC,
					errorMessage: error instanceof Error ? error.message : 'Analytics event failed',
					severity: Severity.MEDIUM,
					source: `${config.source}_analytics`,
					cohort: config.cohort,
					additionalAttributes: {
						experimentId: config.experimentId,
						cohort: config.cohort,
						errorStack: error instanceof Error ? error.stack : undefined,
						errorName: error instanceof Error ? error.name : undefined,
					},
				});
				return false; // failure
			}
		},
		[createAnalyticsEvent, trackError, defaultAdditionalAttributes],
	);

	return {
		trackError,
		fireExperimentExposureEvent,
	};
};
